import type { zMedia } from '@arhs-fleetback/types/entities/media';
import type { z } from 'zod';

import type * as GenericTypes from 'modules/global/generic.types';

// TODO: drop once the package exports the inferred type alongside the zod schema.
type AbstractMedia = z.infer<typeof zMedia>;

// Cameras are served by the camera service. The entity model lives in
// fleetback-data-rest-api/serverless/model/src/cameras/ and is NOT published by
// @arhs-fleetback/types yet — the types below mirror the BE model classes, named after them.
// TODO: drop the mirrors once @arhs-fleetback/types publishes entities/cameras.

export const CameraStatus = {
  ACTIVE: 'ACTIVE',
  DELETED: 'DELETED',
  INACTIVE: 'INACTIVE',
} as const;

export type CameraStatus = (typeof CameraStatus)[keyof typeof CameraStatus];

export const TvDisplayMode = {
  DEFAULT: 'DEFAULT',
  WELCOME: 'WELCOME',
} as const;

export type TvDisplayMode = (typeof TvDisplayMode)[keyof typeof TvDisplayMode];

export const HardwareType = {
  RASPBERRY_PI: 'RASPBERRY_PI',
} as const;

export type HardwareType = (typeof HardwareType)[keyof typeof HardwareType];

// TODO: drop when the package publishes the dealers BasicDealer projection.
export type BasicDealer = {
  id: string;
  name?: string;
};

export type HardwareContext = {
  id: string;
  name: string;
  type: HardwareType;
};

type TvContextBase = {
  id: string;
  name: string;
  pairedDate?: string;
  hardware?: HardwareContext;
  created?: string;
  modified?: string;
};

export type TvContextWelcome = TvContextBase & {
  displayMode: typeof TvDisplayMode.WELCOME;
  withAppointmentMedia?: AbstractMedia;
  withoutAppointmentMedia?: AbstractMedia;
};

type TvContextDefault = TvContextBase & {
  displayMode?: Exclude<TvDisplayMode, typeof TvDisplayMode.WELCOME>;
};

export type TvContext = TvContextWelcome | TvContextDefault;

// Projection returned by GET /cameras/api?dealerId=… — BE `buildCameraContext`
// (model/src/cameras/builders/camera.builder.ts).
export type PaginatedCamera = {
  dealers: Array<BasicDealer>;
  gateId?: string;
  groupId?: string;
  name: string;
  id: string;
  createdOn?: string;
  status: CameraStatus;
  stepId?: string;
  whitelistOnly?: boolean;
  pairedTvs?: Array<TvContext>;
};

// The camera credentials panel is admin-only, hence the optional `config`.
export type CameraCredentials = {
  username?: string;
  password?: string;
};

// Projection returned by GET /cameras/api/{id}, POST, PUT and PATCH status — BE
// `buildEnhancedCameraModel`, which is `buildCameraContext` plus the paired TVs.
export type DetailedCamera = PaginatedCamera & {
  pairedTvs: Array<TvContext>;
  config?: {
    camera?: CameraCredentials;
    dealer?: CameraCredentials;
  };
};

export type CameraScan = {
  cameraId: string;
  registration: string;
  created?: string;
  modified?: string;
};

export type HardwareConfig = {
  osInfo?: string;
  kernelInfo?: string;
  modelInfo?: string;
  storageInfo?: string;
  appVersion?: string;
};

// Projection returned by GET /cameras/api/unlinked-hardware — no explicit BE projection
// class; typed from the BE hardware.entity fields and actual FE usage (id = hardwareId).
export type UnlinkedHardware = {
  id: string;
  name: string;
  type?: HardwareType;
  lastSeen?: string;
  certificateExpiration?: string;
  config?: HardwareConfig;
  // 'UNASSIGNED' sentinel possible (BE: UNASSIGNED_DEALER).
  dealerId?: string;
  enabled?: boolean;
};

// --- FE-only working types (NOT part of the API mirror — never delete with it) ---

// The data reducer enriches the stored camera with its scans (FETCH_CAMERA_SCANS_SUCCESS).
export type StoredCamera = (PaginatedCamera | DetailedCamera) & { scans?: Array<CameraScan> };

export type CamerasPage = { content: Array<PaginatedCamera> };

export type UnlinkedHardwarePage = GenericTypes.PageContent<UnlinkedHardware>;

// BE (joi) types `status` as a single string, but the FE sends arrays through
// filterRequestFormatter — server-side join. The FE also sends a `page` the BE request class
// does not declare. TODO: verify both against the service.
export type CamerasGetRequest = {
  dealerId: string;
  page?: number;
  sort?: string;
  status?: CameraStatus | Array<CameraStatus>;
  text?: string;
};

// TODO: align with the BE camera-create-request once the creation flow is re-verified.
export type CameraCreateData = Omit<PaginatedCamera, 'id' | 'createdOn'>;

// TODO: refine from the pairing modal component at the actions migration.
export type PairTvData = { cameraId: string } & Record<string, unknown>;

export type PatchCameraStatusData = {
  cameraId: string;
  status: CameraStatus;
};

export type CameraScansQueryParams = { cameraId: string } & Record<string, unknown>;

export type CameraScansData = {
  scans: Array<CameraScan>;
  cameraId: string;
};

export type CameraDashboardFilterPayload = {
  field: string;
  value: unknown;
};

export type FetchCamerasAction = GenericTypes.PayloadAction<GenericTypes.GetRequest<CamerasGetRequest>>;
export type FetchCamerasSuccessAction = GenericTypes.PayloadAction<CamerasPage>;

// `cameraId` rides in the payload so the request callbacks can read it back.
export type FetchCameraAction = GenericTypes.PayloadAction<{ cameraId: string } & GenericTypes.GetRequest<undefined>>;
export type FetchCameraSuccessAction = GenericTypes.PayloadAction<DetailedCamera>;
export type FetchCameraFailureAction = GenericTypes.ApiFailureAction;

export type CreateCameraAction = GenericTypes.PayloadAction<GenericTypes.PostRequest<CameraCreateData>>;
export type CreateCameraSuccessAction = GenericTypes.PayloadAction<DetailedCamera>;

export type PutCameraAction = GenericTypes.PayloadAction<GenericTypes.PutRequest<DetailedCamera>>;
export type PutCameraSuccessAction = GenericTypes.PayloadAction<DetailedCamera>;

export type PatchCameraStatusAction = GenericTypes.PayloadAction<
  { cameraId: string } & GenericTypes.PatchRequest<{ status: CameraStatus }>
>;
export type PatchCameraStatusSuccessAction = GenericTypes.PayloadAction<DetailedCamera>;
export type PatchCameraStatusFailureAction = GenericTypes.ApiFailureAction;

export type DeleteCameraAction = GenericTypes.PayloadAction<
  { cameraId: string } & GenericTypes.DeleteRequest<undefined>
>;
export type DeleteCameraSuccessAction = GenericTypes.PayloadAction<string>;
export type DeleteCameraFailureAction = GenericTypes.ApiFailureAction;

export type FetchCameraScansAction = GenericTypes.PayloadAction<
  { cameraId: string } & GenericTypes.GetRequest<CameraScansQueryParams>
>;
export type FetchCameraScansSuccessAction = GenericTypes.PayloadAction<CameraScansData>;

export type FetchUnlinkedHardwareAction = GenericTypes.PayloadAction<
  GenericTypes.GetRequest<Record<string, unknown>>
>;
export type FetchUnlinkedHardwareSuccessAction = GenericTypes.PayloadAction<UnlinkedHardwarePage>;

export type PairTvAction = GenericTypes.PayloadAction<GenericTypes.PostRequest<PairTvData>>;
export type PairTvSuccessAction = GenericTypes.PayloadAction<DetailedCamera>;
export type UnpairTvAction = GenericTypes.PayloadAction<GenericTypes.PostRequest<PairTvData>>;
export type UnpairTvSuccessAction = GenericTypes.PayloadAction<DetailedCamera>;

export type ClearCameraDashboardFiltersAction = GenericTypes.Action<undefined>;
export type SetCameraDashboardFilterAction = GenericTypes.PayloadAction<CameraDashboardFilterPayload>;
export type RemoveCameraDashboardFilterAction = GenericTypes.PayloadAction<CameraDashboardFilterPayload>;

export type OpenPairingModalAction = GenericTypes.PayloadAction<string>;
export type ClosePairingModalAction = GenericTypes.Action<undefined>;
export type SetIsReadyAction = GenericTypes.Action<undefined>;
